import React, { useCallback } from 'react';
import { Palette, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { OnboardingFormData } from '../OnboardingWizardContent';

interface Props {
  data: OnboardingFormData;
  updateData: (updates: Partial<OnboardingFormData>) => void;
}

const StepBrandColors: React.FC<Props> = ({ data, updateData }) => {
  const handleFontUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    updateData({ 
      fontFiles: [...data.fontFiles, ...files],
      fontUrls: [...data.fontUrls, ...files.map(f => URL.createObjectURL(f))]
    });
    e.target.value = '';
  }, [data.fontFiles, data.fontUrls, updateData]);

  const removeFont = (index: number) => {
    updateData({
      fontFiles: data.fontFiles.filter((_, i) => i !== index),
      fontUrls: data.fontUrls.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Optionally specify your brand colors and upload custom fonts.
      </p>

      {/* Color Pickers */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Primary Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={data.primaryColor || '#3B82F6'}
              onChange={(e) => updateData({ primaryColor: e.target.value })}
              className="h-10 w-14 rounded border border-border cursor-pointer"
            />
            <Input
              value={data.primaryColor}
              onChange={(e) => updateData({ primaryColor: e.target.value })}
              placeholder="#3B82F6"
              className="flex-1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Secondary Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={data.secondaryColor || '#10B981'}
              onChange={(e) => updateData({ secondaryColor: e.target.value })}
              className="h-10 w-14 rounded border border-border cursor-pointer"
            />
            <Input
              value={data.secondaryColor}
              onChange={(e) => updateData({ secondaryColor: e.target.value })}
              placeholder="#10B981"
              className="flex-1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Accent Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={data.accentColor || '#F59E0B'}
              onChange={(e) => updateData({ accentColor: e.target.value })}
              className="h-10 w-14 rounded border border-border cursor-pointer"
            />
            <Input
              value={data.accentColor}
              onChange={(e) => updateData({ accentColor: e.target.value })}
              placeholder="#F59E0B"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Font Uploads */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Custom Fonts (Optional)</label>
        
        {data.fontFiles.length > 0 && (
          <div className="space-y-2 mb-4">
            {data.fontFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Palette className="h-5 w-5 text-brand-teal" />
                <span className="flex-1 text-sm text-foreground">{file.name}</span>
                <Button variant="ghost" size="sm" onClick={() => removeFont(index)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
          <div className="flex flex-col items-center">
            <Palette className="h-6 w-6 text-muted-foreground mb-1" />
            <p className="text-sm text-muted-foreground">
              Upload fonts (TTF, OTF, WOFF, WOFF2)
            </p>
          </div>
          <input 
            type="file" 
            className="hidden" 
            accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
            multiple
            onChange={handleFontUpload}
          />
        </label>
      </div>

      <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
        <p className="text-sm text-card-foreground">
          <strong>💡 Tip:</strong> Leave colors blank if you'd like us to extract them 
          from your logo or brand guidelines.
        </p>
      </div>
    </div>
  );
};

export default StepBrandColors;
